import {
  addResources,
  canPayResources,
  getResourceShortfall,
  normalizeResourcePool,
  subtractResources,
} from "../resources/resource-pool";
import { getArmyDefinitionForInstance, getLandDefinitionForInstance } from "./card-instances";
import {
  LANE_IDS,
  type CurrentCoreAlphaGameState,
  type EngineLandCardDefinition,
  type LaneId,
  type LaneSupplySnapshot,
  type ResourcePool,
  type SupplySnapshot,
} from "../types";

const ZERO_RESOURCES = normalizeResourcePool();

function inactiveLaneSnapshot(
  state: Readonly<CurrentCoreAlphaGameState>,
  laneId: LaneId,
): LaneSupplySnapshot {
  const side = state.lanes[laneId].players[state.activePlayerId];
  return {
    laneId,
    active: false,
    landTypeId: null,
    landStage: null,
    production: ZERO_RESOURCES,
    upkeep: ZERO_RESOURCES,
    shortfall: ZERO_RESOURCES,
    remainder: ZERO_RESOURCES,
    sustainable: true,
    armyCardInstanceIds: side.armies.map((army) => army.cardInstanceId),
    discardableArmyCardInstanceIds: [],
  };
}

export function getLaneSupplySnapshot(
  state: Readonly<CurrentCoreAlphaGameState>,
  laneId: LaneId,
): LaneSupplySnapshot {
  const lane = state.lanes[laneId];
  const side = lane.players[state.activePlayerId];
  if (lane.result !== "active" || side.status !== "active" || side.land === null) {
    return inactiveLaneSnapshot(state, laneId);
  }

  const baseLandCardInstanceId = side.land.cardInstanceIds[0];
  const landDefinition = (
    baseLandCardInstanceId === undefined
      ? undefined
      : getLandDefinitionForInstance(state, baseLandCardInstanceId)
  ) as EngineLandCardDefinition | undefined;
  if (landDefinition === undefined) {
    throw new Error(`Active ${laneId} Land does not resolve to a catalog-schema-2 definition.`);
  }

  const stage = landDefinition.stages[side.land.stage - 1];
  if (stage === undefined) {
    throw new Error(`Active ${laneId} Land Stage does not define production.`);
  }

  let upkeep: ResourcePool = ZERO_RESOURCES;
  const armyCardInstanceIds = side.armies.map((army) => army.cardInstanceId);
  for (const armyCardInstanceId of armyCardInstanceIds) {
    const definition = getArmyDefinitionForInstance(state, armyCardInstanceId);
    if (definition === undefined) {
      throw new Error(
        `Army ${armyCardInstanceId} in ${laneId} does not resolve to a catalog-schema-2 Army definition.`,
      );
    }
    upkeep = addResources(upkeep, definition.upkeep);
  }

  const production = normalizeResourcePool(stage.production);
  const sustainable = canPayResources(production, upkeep);
  const shortfall = getResourceShortfall(production, upkeep);
  const remainder = subtractResources(production, upkeep);

  return {
    laneId,
    active: true,
    landTypeId: landDefinition.landTypeId,
    landStage: side.land.stage,
    production,
    upkeep,
    shortfall,
    remainder,
    sustainable,
    armyCardInstanceIds,
    discardableArmyCardInstanceIds: sustainable
      ? []
      : [...armyCardInstanceIds].sort((left, right) => (left < right ? -1 : left > right ? 1 : 0)),
  };
}

export function getSupplySnapshot(state: Readonly<CurrentCoreAlphaGameState>): SupplySnapshot {
  const lanes = {
    left: getLaneSupplySnapshot(state, "left"),
    center: getLaneSupplySnapshot(state, "center"),
    right: getLaneSupplySnapshot(state, "right"),
  };
  const unsustainableLaneIds = LANE_IDS.filter((laneId) => !lanes[laneId].sustainable);
  const kingdomPoolContribution =
    unsustainableLaneIds.length === 0
      ? LANE_IDS.reduce(
          (total, laneId) => addResources(total, lanes[laneId].remainder),
          ZERO_RESOURCES,
        )
      : ZERO_RESOURCES;

  return {
    playerId: state.activePlayerId,
    status: state.supplyStatus,
    lanes,
    unsustainableLaneIds,
    kingdomPoolContribution,
  };
}
